import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Loader2, Lock, Gift } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { BasarNav } from "../components/basar/BasarNav";
import { ImageUploadField } from "../components/basar/ImageUploadField";
import { PrizeGallery } from "../components/basar/PrizeGallery";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { api, errorMessage } from "../lib/api";
import { nok, useMyBasar } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

const MAX_PRIZES = 50;
const newPrize = () => ({ id: "", name: "", description: "", image_url: "", value: "", count: "1", _key: Math.random().toString(36).slice(2) });
const fromServer = (p) => ({ id: p.id, name: p.name || "", description: p.description || "", image_url: p.image_url || "", value: p.value ? String(p.value) : "", count: String(p.count || 1), _key: p.id });

// /dashboard/basar/:id/premiar – premiar med bilete (opplasting), verdi og tal eksemplar. Heile lista blir lagra samla.
export default function BasarPrizesPage() {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const { basar, loading, error, setBasar } = useMyBasar(id);
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (basar && rows === null) setRows((basar.prizes || []).map(fromServer));
  }, [basar, rows]);
  useEffect(() => {
    if (error === "notfound") navigate("/dashboard/basar");
  }, [error, navigate]);

  const locked = !!basar?.draw_published_at;
  const update = (key, patch) => {
    setDirty(true);
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  };
  const move = (idx, dir) => {
    setDirty(true);
    setRows((rs) => {
      const next = [...rs];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return rs;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };
  const remove = (key) => {
    setDirty(true);
    setRows((rs) => rs.filter((r) => r._key !== key));
  };
  const add = () => {
    setDirty(true);
    setRows((rs) => [...rs, newPrize()]);
    setTimeout(() => document.getElementById(`prize-name-${rows?.length || 0}`)?.focus(), 50);
  };

  const invalid = (rows || []).filter((r) => r.name.trim().length < 1 || !(parseInt(r.count, 10) >= 1));
  const save = async () => {
    if (invalid.length) {
      toast.error(t("Alle premier må ha navn og minst ett eksemplar"));
      return;
    }
    setSaving(true);
    try {
      const payload = { prizes: rows.map((r) => ({ id: r.id || "", name: r.name.trim(), description: r.description.trim(), image_url: r.image_url, value: Math.max(0, parseInt(r.value, 10) || 0), count: Math.max(1, parseInt(r.count, 10) || 1) })) };
      const { data } = await api.put(`/my/basar/${id}/prizes`, payload);
      setBasar(data.basar);
      setRows((data.basar.prizes || []).map(fromServer));
      setDirty(false);
      toast.success(t("Premiene er lagret"));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const totalValue = (rows || []).reduce((s, r) => s + (parseInt(r.value, 10) || 0) * (parseInt(r.count, 10) || 1), 0);
  const copies = (rows || []).reduce((s, r) => s + (parseInt(r.count, 10) || 1), 0);
  const previewPrizes = (rows || []).map((r) => ({ id: r._key, name: r.name || t("Premie uten navn"), description: r.description, image_url: r.image_url, value: parseInt(r.value, 10) || 0, count: parseInt(r.count, 10) || 1 }));

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="basar-prizes-page">
        <BasarNav basar={basar} actions={!locked && rows && <Button onClick={save} disabled={saving || !dirty} data-testid="basar-prizes-save-button">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} {saving ? t("Lagrer …") : t("Lagre premier")}</Button>} />
        {loading || rows === null ? (
          <div className="space-y-4" data-testid="basar-loading-state">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
            <div className="lg:col-span-7 space-y-4">
              {locked && <Alert data-testid="basar-prizes-locked"><Lock className="h-4 w-4" /><AlertTitle>{t("Trekningen er publisert")}</AlertTitle><AlertDescription>{t("Premiene kan ikke endres etter at vinnerlisten er publisert.")}</AlertDescription></Alert>}
              {rows.length === 0 && (
                <Alert className="bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30" data-testid="basar-empty-state">
                  <Gift className="h-4 w-4" />
                  <AlertTitle>{t("Ingen premier ennå")}</AlertTitle>
                  <AlertDescription>{t("Legg til minst én premie før basaren åpnes. Hver premie kan ha flere eksemplarer – da trekkes det like mange vinnere.")}</AlertDescription>
                </Alert>
              )}
              {rows.map((r, idx) => (
                <Card key={r._key} className="border-border" data-testid="basar-prize-row" data-index={idx}>
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-muted-foreground">{t("Premie {n}", { n: idx + 1 })}</p>
                      {!locked && (
                        <div className="flex items-center gap-1">
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(idx, -1)} disabled={idx === 0} aria-label={t("Flytt opp")} data-testid="basar-prize-move-up"><ArrowUp className="w-4 h-4" /></Button>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} aria-label={t("Flytt ned")} data-testid="basar-prize-move-down"><ArrowDown className="w-4 h-4" /></Button>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" onClick={() => remove(r._key)} aria-label={t("Fjern premie")} data-testid="basar-prize-remove"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      )}
                    </div>
                    <div className="grid gap-4 sm:grid-cols-12">
                      <div className="sm:col-span-8 space-y-1.5">
                        <Label htmlFor={`prize-name-${idx}`}>{t("Navn på premien")}</Label>
                        <Input id={`prize-name-${idx}`} value={r.name} onChange={(e) => update(r._key, { name: e.target.value })} maxLength={120} placeholder={t("F.eks. Gavekort Kiwi 1000 kr")} disabled={locked} data-testid="basar-prize-name-input" />
                      </div>
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label htmlFor={`prize-value-${idx}`}>{t("Verdi (kr)")}</Label>
                        <Input id={`prize-value-${idx}`} type="number" min={0} inputMode="numeric" value={r.value} onChange={(e) => update(r._key, { value: e.target.value })} placeholder="0" disabled={locked} data-testid="basar-prize-value-input" />
                      </div>
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label htmlFor={`prize-count-${idx}`}>{t("Antall")}</Label>
                        <Input id={`prize-count-${idx}`} type="number" min={1} max={100} inputMode="numeric" value={r.count} onChange={(e) => update(r._key, { count: e.target.value })} disabled={locked} data-testid="basar-prize-count-input" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`prize-desc-${idx}`}>{t("Beskrivelse (valgfritt)")}</Label>
                      <Textarea id={`prize-desc-${idx}`} value={r.description} onChange={(e) => update(r._key, { description: e.target.value })} rows={2} maxLength={600} disabled={locked} data-testid="basar-prize-description-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t("Bilde")}</Label>
                      <ImageUploadField value={r.image_url} onChange={(url) => update(r._key, { image_url: url })} size="w-full sm:w-44" testId={`basar-prize-image-${idx}`} disabled={locked} hint={t("JPG, PNG eller WEBP, maks 5 MB. Et godt bilde selger flere lodd.")} />
                    </div>
                  </CardContent>
                </Card>
              ))}
              {!locked && (
                <div className="flex flex-wrap items-center gap-3">
                  <Button type="button" variant="outline" onClick={add} disabled={rows.length >= MAX_PRIZES} data-testid="basar-prize-add-button"><Plus className="w-4 h-4 mr-1.5" /> {t("Legg til premie")}</Button>
                  {rows.length > 0 && <Button type="button" onClick={save} disabled={saving || !dirty} data-testid="basar-prizes-save-button-bottom">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} {t("Lagre premier")}</Button>}
                  {dirty && <span className="text-xs text-[hsl(var(--warning))]" data-testid="basar-prizes-unsaved">{t("Ulagrede endringer")}</span>}
                </div>
              )}
            </div>

            <div className="lg:col-span-5 space-y-3 lg:sticky lg:top-24">
              <div className="flex items-baseline justify-between">
                <p className="text-sm font-medium text-muted-foreground">{t("Slik vises premiene")}</p>
                <p className="text-xs text-muted-foreground tabular-nums" data-testid="basar-prizes-summary-line">{rows.length === 1 ? t("{n} premie", { n: 1 }) : t("{n} premier", { n: rows.length })}{copies !== rows.length ? ` · ${t("{n} eksemplarer", { n: copies })}` : ""} · {t("verdi {amount}", { amount: nok(totalValue) })}</p>
              </div>
              {previewPrizes.length ? <PrizeGallery prizes={previewPrizes} compact testId="basar-prizes-preview" /> : <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">{t("Premiene dukker opp her etter hvert som du legger dem til.")}</div>}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
